import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import '../../../../assests/css/admin/AdminProfileSetting/AdminAccountSetting/AdminAccountSetting.css';

const AdminAccountSetting = () => {
  const { baseUrl } = useContext(AppContext);

  const [adminId, setAdminId] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');

  const [notifications, setNotifications] = useState({
    orderUpdates:       false,
    securityAlerts:     false,
    stockNotifications: false,
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password:     '',
    confirm_password: '',
  });
  const [passMessage, setPassMessage] = useState({ text: '', type: '' });
  const [passLoading, setPassLoading] = useState(false);

  const token       = localStorage.getItem('adminToken');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // ── READ: Page load par admin ID lo, phir notification prefs fetch karo ──
  useEffect(() => {
    // Step 1: admin auth se ID lo
    axios.get(`${baseUrl}/admin/me`, authHeaders)
            .then((res) => {
        if (res && res.data.success) {
          setNotifications({
            orderUpdates:       !!res.data.data.app_notification,
            securityAlerts:     !!res.data.data.email_notification,
            stockNotifications: !!res.data.data.stock_notification,
          });
        }
      })
      .catch(() => {});
  }, []);

  // ── Toggle: sirf local state change hoga ──
  const handleToggle = (key) =>
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── UPDATE: Notifications save karo DB mein ──
  const handleSaveNotifications = () => {
    setNotifLoading(true);
    setNotifMessage('');
    axios.post(`${baseUrl}/admin/notification-prefs`, {
      id:                  adminId,
      app_notification:    notifications.orderUpdates       ? 1 : 0,
      email_notification:  notifications.securityAlerts     ? 1 : 0,
      stock_notification:  notifications.stockNotifications ? 1 : 0,
    }, authHeaders)
      .then((res) => {
        if (res.data.success) setNotifMessage('Settings saved successfully!');
        setNotifLoading(false);
      })
      .catch(() => {
        setNotifMessage('Failed to save settings.');
        setNotifLoading(false);
      });  };

  // ── Password field change ──
  const handlePassChange = (e) =>
    setPasswords((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // ── UPDATE: Password change karo DB mein ──
  const handlePassSubmit = () => {
    setPassMessage({ text: '', type: '' });

    // Frontend validation
    if (!passwords.current_password || !passwords.new_password || !passwords.confirm_password) {
      setPassMessage({ text: 'All fields are required.', type: 'error' });
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      setPassMessage({ text: 'New password and confirm password do not match.', type: 'error' });
      return;
    }
    if (passwords.new_password.length < 6) {
      setPassMessage({ text: 'New password must be at least 6 characters.', type: 'error' });
      return;
    }

    setPassLoading(true);
    axios.post(`${baseUrl}/admin/profile/changePassword`, {
      id:               adminId,
      current_password: passwords.current_password,
      new_password:     passwords.new_password,
      confirm_password: passwords.confirm_password,
    }, authHeaders)
      .then((res) => {
        if (res.data.success) {
          setPassMessage({ text: 'Password changed successfully!', type: 'success' });
          setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        } else {
          setPassMessage({ text: res.data.message || 'Failed to change password.', type: 'error' });
        }
        setPassLoading(false);
      })
      .catch(() => {
        setPassMessage({ text: 'Something went wrong. Please try again.', type: 'error' });
        setPassLoading(false);
      });  };

  const notifItems = [
    { key: 'orderUpdates',       title: 'Order Updates',       desc: 'Receive emails for every new order placed in your store.' },
    { key: 'securityAlerts',     title: 'Security Alerts',     desc: 'Immediate notifications for account logins from new devices.' },
    { key: 'stockNotifications', title: 'Stock Notifications', desc: 'Get notified when products in your inventory are running low.' },
  ];

  return (
    <div className="admin-account-setting">

      {/* ── SECTION 1: Email Notification Preferences ── */}
      <div className="settings-header">
        <h1>Account Settings</h1>
        <p>Manage your notification preferences.</p>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <h2>Email Notification Preferences</h2>
        </div>

        {notifItems.map(({ key, title, desc }) => (
          <div className="notification-item" key={key}>
            <div className="notification-info">
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notifications[key]}
                onChange={() => handleToggle(key)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        ))}

        {notifMessage && <p className="success-msg">{notifMessage}</p>}

        <div className="settings-footer">
          <button className="btn-save-settings" onClick={handleSaveNotifications} disabled={notifLoading}>
            {notifLoading ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </div>
      </div>

      {/* ── SECTION 2: Change Password ── */}
      <div className="settings-header section-gap">
        <h1>Privacy & Security</h1>
        <p>Manage your password and account security settings.</p>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <h2>Change Password</h2>
        </div>

        {passMessage.text && (
          <p className={passMessage.type === 'success' ? 'success-msg' : 'error-msg'}>
            {passMessage.text}
          </p>
        )}

        <div className="setting-item">
          <label>Current Password</label>
          <input
            type="password"
            name="current_password"
            value={passwords.current_password}
            onChange={handlePassChange}
            placeholder="Enter current password"
            className="security-input"
          />
        </div>

        <div className="setting-item">
          <label>New Password</label>
          <input
            type="password"
            name="new_password"
            value={passwords.new_password}
            onChange={handlePassChange}
            placeholder="Enter new password"
            className="security-input"
          />
        </div>

        <div className="setting-item">
          <label>Confirm New Password</label>
          <input
            type="password"
            name="confirm_password"
            value={passwords.confirm_password}
            onChange={handlePassChange}
            placeholder="Confirm new password"
            className="security-input"
          />
        </div>

        <div className="settings-footer">
          <button className="btn-save-settings" onClick={handlePassSubmit} disabled={passLoading}>
            {passLoading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default AdminAccountSetting;